// lib/api/tara/notifications/shop_insights_api_calls.ts
import { asyncHandler } from '../../async-handler';
import store from '../../../../store/store';
import { RequestInfo } from '../../../../interfaces/RequestInfo';
import { applicationJSONBodyBuilder } from '../../../utils/body-builder';
import {
  TARA_NOTIFICATIONS_UPDATE_API,
  TARA_NOTIFICATIONS_UPDATE_ORDER_API,
} from '../../../constants/central-api';

/**
 * Add insight to shop (enable insight notifications)
 * POST /api/v1/tara/shops/:slug/insights  { insight_id: number }
 */
export async function add_shop_insight_api_call(insight_id: number | string) {
  const shop_slug = store.getState().user.company.shop_slug;
  const accessToken = store.getState().auth.authorization.accessToken;
  const isWhiteLabel = store.getState().UI?.isWhiteLabel;
  const sellerToken = isWhiteLabel
    ? accessToken?.currentWhiteLabelSellerToken
    : accessToken?.sellerToken;

  const requestInfo: RequestInfo = {
    url: TARA_NOTIFICATIONS_UPDATE_API(shop_slug),
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-type': 'application/json',
      Authorization: accessToken.type + ' ' + sellerToken,
    },
    body: applicationJSONBodyBuilder({ insight_id: Number(insight_id) }),
  };

  const actionType = 'ADD_SHOP_INSIGHT_API';
  const customMsg = 'Shop insight successfully added.';

  const res: any = await asyncHandler(
    requestInfo,
    actionType,
    customMsg,
    false
  );
  const response = res?.data;

  if (res.actionType === actionType && response?.success === true) {
    return true;
  }
  return null;
}

/**
 * Remove shop insight (disable insight notifications)
 * DELETE /api/v1/tara/shops/:slug/insights/:id
 * Note: :id is the shop-insight id returned in "activated items" list.
 */
export async function delete_shop_insight_api_call(
  shop_insight_id: number | string
) {
  const shop_slug = store.getState().user.company.shop_slug;
  const accessToken = store.getState().auth.authorization.accessToken;
  const isWhiteLabel = store.getState().UI?.isWhiteLabel;
  const sellerToken = isWhiteLabel
    ? accessToken?.currentWhiteLabelSellerToken
    : accessToken?.sellerToken;

  const requestInfo: RequestInfo = {
    url: `${TARA_NOTIFICATIONS_UPDATE_API(shop_slug)}/${shop_insight_id}`,
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      Authorization: accessToken.type + ' ' + sellerToken,
    },
  };

  const actionType = 'DELETE_SHOP_INSIGHT_API';
  const customMsg = 'Shop insight successfully removed.';

  const res: any = await asyncHandler(
    requestInfo,
    actionType,
    customMsg,
    false
  );
  const response = res?.data;

  // Some DELETEs may not return a "success" boolean; treat 2xx as success via handler
  if (
    res.actionType === actionType &&
    (response?.success === true || res?.ok === true)
  ) {
    return true;
  }
  return null;
}

/**
 * Update order of shop insights/prompt/graphs
 * POST /api/v1/tara/shops/:slug/insights/update-order
 *
 * payload: { ordered_items: [ "insight_18", "insight_111", ... ] }
 */
export default async function update_insights_order_api_call(
  ordered_items: string[]
) {
  const shop_slug = store.getState().user.company.shop_slug;
  const accessToken = store.getState().auth.authorization.accessToken;
  const isWhiteLabel = store.getState().UI?.isWhiteLabel;
  const sellerToken = isWhiteLabel
    ? accessToken?.currentWhiteLabelSellerToken
    : accessToken?.sellerToken;

  const requestInfo: RequestInfo = {
    url: `${TARA_NOTIFICATIONS_UPDATE_ORDER_API(shop_slug)}`,
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-type': 'application/json',
      Authorization: accessToken.type + ' ' + sellerToken,
    },
    body: applicationJSONBodyBuilder({ ordered_items }),
  };

  const actionType = 'UPDATE_INSIGHTS_ORDER_API';
  const customMsg = 'Shop insights order updated.';

  const res: any = await asyncHandler(
    requestInfo,
    actionType,
    customMsg,
    false
  );
  const response = res?.data;

  if (
    res.actionType === actionType &&
    (response?.success === true || res?.ok === true)
  ) {
    return true;
  }
  // throw an error so callers can handle revert logic
  throw new Error(response?.message || 'Failed to update order');
}
